'use server'

import { publicError } from '@/lib/action-errors';
import { query } from '@/lib/db';
import crypto from 'crypto';
import { headers } from 'next/headers';
import { getSession, getSessionStatus } from '@/lib/session';
import {
  canAutoPublish,
  canEditWithoutReview,
  ownerStatusChangeError,
  activationExpirySql,
  isUnclaimedDraft,
  isUuid,
  DRAFT_STATUS,
  makeListingSlug,
  EXPIRY_ON_ACTIVATION_SQL,
} from '@/lib/listing-policy';
import { sendListingApprovedEmail, sendListingRejectedEmail } from '@/lib/listing-emails';
import { getSiteUrl } from '@/lib/site-url';
import { knownPropertyType, normalizePropertyType } from '@/lib/property-types';
import { validateListing, normalizeListing } from '@/lib/listing-input';
import { listingTypeOf } from '@/lib/rental-terms';
import { findProvince } from '@/lib/provinces';
import { canonicalAmenity } from '@/lib/listing-labels';
import { isKnownPlace, provinceOfPlaceElsewhere, UNKNOWN_PLACE_ERROR } from '@/lib/known-places';
import { plainText } from '@/lib/plain-text';
import { formatCanadianPhone } from '@/lib/phone';
import { getAgencyContext, canManageListing } from '@/lib/agency-access';
import { rateLimit } from '@/lib/rate-limit';
import { ipFromHeaders } from '@/lib/request-ip';
import { isIpBanned } from '@/lib/banned-ips';
import { removeUnreferencedUploads } from '@/lib/uploads-cleanup';
import { replaceListingPhotos, MAX_LISTING_PHOTOS } from '@/lib/listing-photos';
import { resolveSpecialist } from '@/lib/agency-specialists';
import { importNumber, importInteger, importBoolean, importIdentity } from '@/lib/import-values';
import { PROPERTY_STATUSES } from '@/lib/enum-columns';
import { publicListingRows } from '@/lib/listing-privacy';
import { EMPTY_SUGGESTIONS, searchSuggestions } from '@/lib/search-suggest';
import { listingPathFor } from '@/lib/place-url';


// A rental's terms (rentals, step 3, 2026-09-24), written by every path that
// saves a listing: the kind, and for a rental its date, lease, furniture, what
// the rent includes and a deposit - none on a sale, none in Quebec.
const RENTAL_COLUMNS = ['listing_type', 'available_from', 'lease_term', 'furnished', 'rent_includes', 'deposit_amount'] as const;
function rentalValues(data: Record<string, any>): unknown[] {
  const n = normalizeListing(data);
  return [n.listing_type, n.available_from, n.lease_term, n.furnished, n.rent_includes, n.deposit_amount];
}
const rentalSet = (first: number) => RENTAL_COLUMNS.map((c, i) => `${c} = $${first + i}`).join(', ');
const rentalParams = (first: number) => RENTAL_COLUMNS.map((_, i) => `$${first + i}`).join(', ');

export async function syncFavoritesServer(favorites: string[]) {
  const session = await getSession();
  if (!session || !session.userId) return { success: false, error: 'Not authenticated' };
  
  // A list of listing ids, at most 500 of them: the action takes whatever JSON a hand-made request sends, and the column is read on
  // every page the person opens.
  const clean = [...new Set((Array.isArray(favorites) ? favorites : []).filter((v): v is string => typeof v === 'string' && isUuid(v)).map((v) => v.toLowerCase()))].slice(0, 500);
  await query(
    `UPDATE users SET favorites = $1 WHERE id = $2`,
    [JSON.stringify(clean), session.userId]
  );
  return { success: true };
}

export async function getFavoritesServer() {
  const session = await getSession();
  if (!session || !session.userId) return { success: false, favorites: [] };
  
  const res = await query(`SELECT favorites FROM users WHERE id = $1`, [session.userId]);
  return { success: true, favorites: res.rows[0]?.favorites || [] };
}

export async function getUserPropertiesServer() {
  try {
    const statusRes = await getSessionStatus();
    const session = statusRes.status === 'authenticated' ? statusRes.session : null;

    if (!session?.userId) {
      return { success: false, properties: [], isAuth: false, isExpired: statusRes.status === 'expired' };
    }

    const res = await query(
      `SELECT p.*, COALESCE(
         (SELECT json_agg(ph.url ORDER BY ph.display_order, ph.id) FROM photos ph WHERE ph.property_id = p.id),
         '[]'::json
       ) as photos
       FROM properties p
       WHERE p.user_id = $1
         AND p.status != 'deleted'
         AND NOT (p.status = $2 AND p.approved_at IS NULL AND p.title_en = 'Draft')
       ORDER BY p.created_at DESC`,
      [session.userId, DRAFT_STATUS]
    );

    return { success: true, properties: res.rows, isAuth: true, email: session.email };
  } catch (error: any) {
    console.error('Error fetching user properties from server DB:', error);
    return { success: false, properties: [], error: publicError(error) };
  }
}

export async function getUserProfileServer() {
  try {
    const session = await getSession();
    if (!session?.email) {
      return { success: false, isAuth: false, profile: null };
    }
    const res = await query(
      `SELECT id, email, contact_email, name, phone, city, province, (password_hash IS NOT NULL) AS has_password FROM users WHERE LOWER(email) = LOWER($1)`,
      [session.email]
    );
    if (res.rows.length === 0) {
      return { 
        success: true, 
        isAuth: true, 
        profile: { email: session.email, contact_email: '', name: session.name || '', phone: '', city: '', province: '' } 
      };
    }
    // contact_email as stored: an empty one stays empty in the form, with the
    // login address as its placeholder; filling it in here made every profile
    // save write it (seller check, 2026-09-27).
    const userRow = res.rows[0];
    return { success: true, isAuth: true, profile: userRow };
  } catch (error: any) {
    console.error('Error fetching user profile:', error);
    return { success: false, isAuth: false, error: publicError(error) };
  }
}

// The seller's own details. A field that is sent is written as it is (an empty one clears it); one that is not
// sent stays. What is written is checked in the person's language, and the answer says which field is wrong.
export async function updateUserProfileServer(data: { name?: string; phone?: string; city?: string; province?: string; contact_email?: string; lang?: string }) {
  try {
    const session = await getSession();
    if (!session?.email) {
      return { success: false, error: 'Unauthorized' };
    }
    const fr = data.lang === 'fr';
    const say = (en: string, frText: string) => (fr ? frText : en);
    const has = (k: keyof typeof data) => typeof data[k] === 'string';
    const values: Record<string, string | null> = {};
    if (has('name')) {
      const name = plainText(data.name).slice(0, 255);
      if (name.length > 100) return { success: false, field: 'name', error: say('The name is at most 100 characters.', 'Le nom compte au plus 100 caractères.') };
      values.name = name || null;
    }
    if (has('phone')) {
      const phone = formatCanadianPhone(String(data.phone).trim());
      const n = phone.replace(/\D/g, "").length;
      if (phone && (n < 10 || n > 15)) return { success: false, field: 'phone', error: say('Enter a phone number with area code, or leave it empty.', 'Indiquez un numéro avec l’indicatif régional, ou laissez vide.') };
      values.phone = phone || null;
    }
    if (has('contact_email')) {
      const email = String(data.contact_email).trim().toLowerCase();
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) return { success: false, field: 'contact_email', error: say('Enter a valid email address, or leave it empty.', 'Indiquez une adresse courriel valide, ou laissez vide.') };
      values.contact_email = email || null;
    }
    let provinceCode: string | null = null;
    if (has('province')) {
      const raw = String(data.province).trim();
      if (raw && !findProvince(raw)) return { success: false, field: 'province', error: say('Choose a province from the list.', 'Choisissez une province dans la liste.') };
      provinceCode = raw ? findProvince(raw)!.code : null;
      values.province = provinceCode;
    }
    if (has('city')) {
      const city = plainText(data.city).slice(0, 100);
      values.city = city || null;
      // "Vancouver" with Quebec is a slip, not a village the list lacks.
      if (city && provinceCode) {
        const elsewhere = provinceOfPlaceElsewhere(city, provinceCode);
        if (elsewhere && !(await isKnownPlace(city, provinceCode))) {
          const there = findProvince(elsewhere);
          return { success: false, field: 'city', error: say(`${city} is in ${there?.en || elsewhere}, not in ${findProvince(provinceCode)?.en || provinceCode}. Check the province.`, `${city} se trouve en ${there?.fr || elsewhere}, pas en ${findProvince(provinceCode)?.fr || provinceCode}. Vérifiez la province.`) };
        }
      }
    }
    const columns = Object.keys(values);
    if (columns.length > 0) {
      const sets = columns.map((c, i) => `${c} = $${i + 1}`).join(', ');
      await query(`UPDATE users SET ${sets} WHERE LOWER(email) = LOWER($${columns.length + 1})`, [...columns.map((c) => values[c]), session.email]);
    }
    return { success: true };
  } catch (error: any) {
    console.error('Error updating user profile:', error);
    return { success: false, error: publicError(error) };
  }
}

// Who may change a listing: an admin, the account that published it, or a member
// of the agency that owns it (a consultant only for listings they wrote). The
// contact email is free text typed by the author, so it never grants rights.
async function verifyAdminOrOwner(propertyId: string): Promise<{ authorized: boolean; isAdmin: boolean; email?: string; error?: string }> {
  const session = await getSession();
  if (!session) {
    return { authorized: false, isAdmin: false, error: 'Unauthorized: Session required' };
  }
  if (session.role === 'admin') {
    return { authorized: true, isAdmin: true, email: session.email };
  }
  if (!isUuid(propertyId)) {
    return { authorized: false, isAdmin: false, error: 'Property not found' };
  }
  const propRes = await query(`SELECT p.user_id, p.created_by FROM properties p WHERE p.id = $1`, [propertyId]);
  if (propRes.rows.length === 0) {
    return { authorized: false, isAdmin: false, error: 'Property not found' };
  }
  const prop = propRes.rows[0];
  if (session.userId && prop.user_id === session.userId) {
    return { authorized: true, isAdmin: false, email: session.email };
  }

  const ctx = await getAgencyContext(prop.user_id);
  if (ctx && canManageListing(ctx, prop)) {
    return { authorized: true, isAdmin: ctx.isAdmin, email: session.email };
  }
  return { authorized: false, isAdmin: false, error: 'Forbidden: You do not have permission to modify this listing' };
}

// A listing an agency's consultant or manager made belongs to the agency (its owner gets the
// mail), but the person who wrote it is the one who has to act on a decision, so they get it too.
async function listingAuthorEmail(id: string): Promise<string | null> {
  const res = await query(
    `SELECT author.email FROM properties p
     JOIN users author ON author.id = p.created_by
     WHERE p.id = $1 AND p.created_by IS DISTINCT FROM p.user_id
       AND author.deleted_at IS NULL AND author.is_blocked IS NOT TRUE`,
    [id]
  ).catch(() => null);
  return res?.rows[0]?.email || null;
}

// Tells the owner by email that the listing is online.
async function emailListingApproved(id: string) {
  try {
    const res = await query(
      `SELECT COALESCE(u.email, p.contact_email) AS email, p.title_en, p.title_fr, p.province, p.city, p.slug, p.listing_type, p.expires_at
       FROM properties p LEFT JOIN users u ON u.id = p.user_id
       WHERE p.id = $1`,
      [id]
    );
    const row = res.rows[0];
    if (!row?.email) return;
    const site = await getSiteUrl();
    // Each language's own address (lib/place-url.ts): lowercase, a rental under its branch.
    const author = await listingAuthorEmail(id);
    const recipients = [row.email, ...(author && author.toLowerCase() !== String(row.email).toLowerCase() ? [author] : [])];
    for (const to of recipients) {
      await sendListingApprovedEmail({
        to,
        titleEn: row.title_en || row.title_fr || 'Listing',
        titleFr: row.title_fr || row.title_en || 'Annonce',
        urlEn: `${site}${listingPathFor('en', row)}`,
        urlFr: `${site}${listingPathFor('fr', row)}`,
        expiresAt: row.expires_at ? new Date(row.expires_at) : null,
      });
    }
  } catch (e) {
    console.error('Error emailing listing approval:', e);
  }
}

async function verifyAdmin(): Promise<{ authorized: boolean; error?: string }> {
  const session = await getSession();
  if (!session || session.role !== 'admin') {
    return { authorized: false, error: 'Forbidden: Admin access required' };
  }
  return { authorized: true };
}

// Creates the draft the listing form uploads photos into. The owner is taken from
// the session only; anonymous drafts stay ownerless until published.
export async function createHiddenProperty() {
  const session = await getSession();
  const uid = session?.userId || null;

  // One person can only have so many unfinished drafts: each one is a folder of
  // uploaded photos on disk.
  const headersList = await headers();
  const ip = ipFromHeaders((name) => headersList.get(name));
  if (await isIpBanned(ip)) {
    throw new Error('Forbidden');
  }
  if (!(await rateLimit(`draft:${session?.userId || ip}`, 20, 60 * 60 * 1000)).ok) {
    throw new Error('Too many listings started. Please finish one first.');
  }

  const id = crypto.randomUUID();
  await query(
    `INSERT INTO properties (id, user_id, created_by, status, title_en, title_fr, description_en, description_fr, price, province, city, slug)
     VALUES ($1, $2, $2, $3, 'Draft', 'Brouillon', '', '', 0, '', '', $4) RETURNING id`,
    [id, uid, DRAFT_STATUS, `draft-${id}`]
  );
  return id;
}

export async function publishProperty(id: string, data: any) {
  // Publishing requires a signed-in account: the listing form confirms the email
  // with a one-time code first, so a listing can never be attached to (or grant a
  // session for) an address its author does not control.
  const session = await getSession();
  if (!session?.userId && session?.role !== 'admin') {
    return { success: false, error: 'Please confirm your email before publishing.', needsVerification: true };
  }
  if (!isUuid(id)) {
    return { success: false, error: 'Listing not found' };
  }

  const draftRes = await query(`SELECT id, user_id, status, approved_at FROM properties WHERE id = $1`, [id]);
  const draft = draftRes.rows[0];
  if (!draft) {
    return { success: false, error: 'Listing not found' };
  }
  const isOwnDraft = draft.status === DRAFT_STATUS && !draft.approved_at && draft.user_id === session.userId;
  if (!isUnclaimedDraft(draft) && !isOwnDraft) {
    return { success: false, error: 'This listing has already been published. Use Edit instead.' };
  }

  const lang = data?.lang === 'fr' ? 'fr' : 'en';
  const errors = validateListing({ ...data, contact_email: data?.contact_email || session.email }, lang);
  const firstError = Object.values(errors)[0];
  if (firstError) {
    return { success: false, error: firstError, errors };
  }
  // Only a place the site knows (as for an agency): a city typed with a typo, or in the wrong province,
  // made a page for a place that does not exist. An administrator may write anywhere.
  if (session.role !== 'admin' && !(await isKnownPlace(String(data?.city || ''), String(data?.province || '')))) {
    return { success: false, error: UNKNOWN_PLACE_ERROR, cityUnknown: true, city: String(data?.city || '').trim(), province: String(data?.province || '').trim() };
  }

  // A listing with no photos gets far fewer calls and looks abandoned; the wizard
  // already nudges for one, but the browser's word is not enough on its own.
  const photoCountRes = await query(`SELECT COUNT(*)::int AS count FROM photos WHERE property_id = $1`, [id]);
  if ((photoCountRes.rows[0]?.count || 0) === 0) {
    return {
      success: false,
      error: lang === 'fr' ? 'Ajoutez au moins une photo avant de publier.' : 'Add at least one photo before publishing.',
      missingPhotos: true,
    };
  }

  const input = normalizeListing({ ...data, contact_email: data.contact_email || session.email });

  const userId = session.userId || null;
  const initialStatus = (await canAutoPublish(userId, session)) ? 'active' : 'pending';
  const slug = makeListingSlug(input.title_en, input.title_fr);

  await query(
    `UPDATE properties
     SET
       approved_at = CASE WHEN $21::text = 'active' THEN NOW() ELSE NULL END,
       user_id = COALESCE($1, user_id),
       created_by = COALESCE(created_by, $1),
       title_en = $2, title_fr = $3, 
       description_en = $4, description_fr = $5,
       price = $6, property_type = $7, 
       bedrooms = $8, bathrooms = $9,
       pet_friendly = $10, province = $11, city = $12,
       slug = $13, address = $14, contact_email = $15, contact_phone = $16,
       require_auth_to_contact = $17,
       living_area = $18, year_built = $19, amenities = $20,
       status = $21::text, created_at = NOW(), updated_at = NOW(),
       expires_at = CASE WHEN $21::text = 'active' THEN ${EXPIRY_ON_ACTIVATION_SQL} ELSE NULL END,
       ${rentalSet(23)}
     WHERE id = $22`,
    [
      userId,
      input.title_en, input.title_fr,
      input.description_en, input.description_fr,
      input.price, input.type,
      input.bedrooms, input.bathrooms,
      input.pet_friendly, input.province, input.city,
      slug,
      input.address, input.contact_email, input.contact_phone,
      false,
      input.living_area,
      input.year_built,
      input.amenities,
      initialStatus,
      id,
      ...rentalValues(input),
    ]
  );
  // Without the language: the wizard puts its own in front.
  return { success: true, status: initialStatus, path: listingPathFor('en', { province: input.province, city: input.city, slug, listing_type: input.listing_type }).replace(/^\/en/, '') };
}

// Favorites: publicly visible listings only (sold ones stay visible to the buyer),
// without the sellers' contacts (lib/listing-privacy.ts) - any visitor can call this.
export async function getMyPropertiesByIds(ids: string[]) {
  const validIds = (Array.isArray(ids) ? ids : []).filter(isUuid);
  if (validIds.length === 0) return [];
  const res = await query(
    `SELECT p.*, COALESCE(
       (SELECT json_agg(ph.url ORDER BY ph.display_order, ph.id) FROM photos ph WHERE ph.property_id = p.id),
       '[]'::json
     ) as photos
     FROM properties p
     LEFT JOIN users u ON u.id = p.user_id
     WHERE p.id = ANY($1::uuid[])
       AND p.status IN ('active', 'sold')
       AND COALESCE(p.is_hidden_by_admin, FALSE) = FALSE AND p.deleted_at IS NULL
       -- A blocked seller's listings are off every other surface; a saved one stayed on this one (matrix of 2026-10-15).
       AND (u.is_blocked IS NOT TRUE)`,
    [validIds]
  );
  return publicListingRows(res.rows);
}

// Every status except 'deleted', which is reached by deleting rather than by
// setting it here. Derived from the one list so this cannot name a word the
// database now refuses.
const ADMIN_SETTABLE_STATUSES: string[] = PROPERTY_STATUSES.filter((s) => s !== 'deleted');

export async function updatePropertyStatus(id: string, status: string) {
  const auth = await verifyAdminOrOwner(id);
  if (!auth.authorized) {
    throw new Error(auth.error || 'Forbidden');
  }

  if (auth.isAdmin) {
    if (!ADMIN_SETTABLE_STATUSES.includes(status)) {
      throw new Error('Unknown status');
    }
    const res = await query(
      `UPDATE properties
       SET status = $1::text,
           approved_at = CASE WHEN $1::text = 'active' THEN COALESCE(approved_at, NOW())
                              WHEN $1::text IN ('pending', 'rejected') THEN NULL
                              ELSE approved_at END,
           updated_at = NOW()
       WHERE id = $2 RETURNING *`,
      [status, id]
    );
    return res.rows[0];
  }

  const currentRes = await query(
    `SELECT status, approved_at, is_hidden_by_admin, deleted_at FROM properties WHERE id = $1`,
    [id]
  );
  const denied = currentRes.rows[0] ? ownerStatusChangeError(currentRes.rows[0], status) : 'Listing not found';
  if (denied) {
    throw new Error(denied);
  }

  const res = await query(
    `UPDATE properties SET status = $1::text, updated_at = NOW()${activationExpirySql(status)} WHERE id = $2 RETURNING *`,
    [status, id]
  );
  return res.rows[0];
}

/**
 * A moderator's decision applies to the version of the listing they looked at. A seller who deletes or rewrites it
 * while the card is open must not get the new text approved unseen, or a deleted listing brought back to life.
 * `seen` is the updated_token of the card; null skips the check (nothing is shown to compare, e.g. a bulk action
 * on rows read a moment ago passes theirs too).
 */
async function moderationConflict(id: string, seen: string | null | undefined): Promise<'missing' | 'deleted' | 'changed' | null> {
  const cur = await query(`SELECT status, deleted_at, updated_at::text AS token FROM properties WHERE id = $1`, [id]);
  const row = cur.rows[0];
  if (!row) return 'missing';
  if (row.deleted_at || row.status === 'deleted') return 'deleted';
  if (seen && row.token !== seen) return 'changed';
  return null;
}

export async function approveAndResolveProperty(id: string, seen?: string | null) {
  const auth = await verifyAdmin();
  if (!auth.authorized) return { success: false, error: auth.error };
  if (!isUuid(id)) return { success: false, error: 'missing' };
  const conflict = await moderationConflict(id, seen);
  if (conflict) return { success: false, error: conflict };

  const complaintCount = Number((await query(`SELECT count(*)::int AS n FROM complaints WHERE property_id = $1`, [id])).rows[0]?.n || 0);
  const res = await query(
    `SELECT COALESCE(u.email, p.contact_email) AS owner_email, p.title_en, p.title_fr
     FROM properties p LEFT JOIN users u ON u.id = p.user_id
     WHERE p.id = $1`,
    [id]
  );
  if (res.rows.length > 0) {
    const { owner_email: contact_email, title_en, title_fr } = res.rows[0];

    // The same conditions again in the statement itself: a change between the check above and here changes nothing.
    // The row is locked first and its state before this call is read from the lock: two approvals at once (two
    // administrators, a double click) used to send the owner the good news twice.
    const done = await query<{ was_live: boolean }>(
      `WITH prev AS (SELECT id, status AS prev_status, approved_at AS prev_approved FROM properties WHERE id = $1 FOR UPDATE)
       UPDATE properties SET status = 'active', rejection_reason = NULL, approved_at = NOW(), expires_at = ${EXPIRY_ON_ACTIVATION_SQL}, expiry_reminder_sent_at = NULL, updated_at = NOW()
       FROM prev
       WHERE properties.id = prev.id AND properties.deleted_at IS NULL AND ($2::text IS NULL OR properties.updated_at::text = $2::text)
       RETURNING (prev.prev_status = 'active' AND prev.prev_approved IS NOT NULL) AS was_live`,
      [id, seen || null]
    );
    if (done.rows.length === 0) return { success: false, error: (await moderationConflict(id, seen)) || 'changed' };
    // Approving a listing that is already online (to close the reports about it) is not news to its owner.
    if (!done.rows[0].was_live) await emailListingApproved(id);

    // Clear complaints
    await query(
      `DELETE FROM complaints WHERE property_id = $1`,
      [id]
    );

    if (contact_email) {
      await query(`
        INSERT INTO user_notifications (user_email, property_id, type, title_en, title_fr, message_en, message_fr)
        VALUES ($1, $2, 'moderation_approved', $3, $4, $5, $6)
      `, [
        contact_email,
        id,
        complaintCount > 0 ? 'Listing Approved & Complaints Cleared' : 'Listing approved',
        complaintCount > 0 ? 'Annonce approuvée et signalements résolus' : 'Annonce approuvée',
        complaintCount > 0
          ? `Your listing "${title_en || 'Property'}" has been approved and the reports about it were reviewed and closed.`
          : `Your listing "${title_en || 'Property'}" has been approved and is now online.`,
        complaintCount > 0
          ? `Votre annonce "${title_fr || 'Propriété'}" a été approuvée et les signalements la concernant ont été examinés et clos.`
          : `Votre annonce "${title_fr || 'Propriété'}" a été approuvée et est maintenant en ligne.`
      ]).catch(console.error);
    }

    try {
      const { createAuditLog } = await import('@/lib/audit');
      await createAuditLog('resolve_and_approve_property', 'property', id, { title: title_en || title_fr, status: 'active' });
    } catch {}
  }
  return { success: true };
}

export async function rejectProperty(id: string, reason?: string, seen?: string | null) {
  const auth = await verifyAdmin();
  if (!auth.authorized) return { success: false, error: auth.error };
  if (!isUuid(id)) return { success: false, error: 'missing' };
  const conflict = await moderationConflict(id, seen);
  if (conflict) return { success: false, error: conflict };

  const defaultReason = 'Listing does not comply with publication guidelines';
  const effectiveReason = (reason && reason.trim()) || defaultReason;

  const res = await query(
    `SELECT COALESCE(u.email, p.contact_email) AS contact_email, p.title_en, p.title_fr, COALESCE(u.is_realtor, FALSE) AS agency_owned
     FROM properties p LEFT JOIN users u ON u.id = p.user_id
     WHERE p.id = $1`,
    [id]
  );

  const rejected = await query(
    `UPDATE properties
     SET status = 'rejected',
         rejection_reason = $1,
         approved_at = NULL,
         updated_at = NOW()
     WHERE id = $2 AND deleted_at IS NULL AND ($3::text IS NULL OR updated_at::text = $3::text) RETURNING id`,
    [effectiveReason, id, seen || null]
  );
  if (rejected.rows.length === 0) return { success: false, error: (await moderationConflict(id, seen)) || 'changed' };

  if (res.rows.length > 0 && res.rows[0].contact_email) {
    const { contact_email, title_en, title_fr, agency_owned } = res.rows[0];
    const site = await getSiteUrl();
    // An agency's listing is edited in its cabinet, not on the seller's edit page.
    const author = await listingAuthorEmail(id);
    const recipients = [contact_email, ...(author && author.toLowerCase() !== String(contact_email).toLowerCase() ? [author] : [])];
    for (const to of recipients) {
      await sendListingRejectedEmail({
        to,
        titleEn: title_en || title_fr || 'Listing',
        titleFr: title_fr || title_en || 'Annonce',
        reason: effectiveReason,
        editUrlEn: agency_owned ? `${site}/cabinet/realtor/settings?tab=listings` : `${site}/edit/${id}`,
        editUrlFr: agency_owned ? `${site}/fr/cabinet/realtor/settings?tab=listings` : `${site}/fr/edit/${id}`,
      });
    }
    await query(`
      INSERT INTO user_notifications (user_email, property_id, type, title_en, title_fr, message_en, message_fr)
      VALUES ($1, $2, 'moderation_rejected', $3, $4, $5, $6)
    `, [
      contact_email,
      id,
      'Listing Rejected by Moderator',
      'Annonce refusée par le modérateur',
      `Your listing "${title_en || 'Property'}" was rejected. Reason: ${effectiveReason}`,
      `Votre annonce "${title_fr || 'Propriété'}" a été refusée. Motif : ${effectiveReason}`
    ]).catch(console.error);
  }

  try {
    const { createAuditLog } = await import('@/lib/audit');
    await createAuditLog('reject_property', 'property', id, { reason: effectiveReason, title: res.rows[0]?.title_en || res.rows[0]?.title_fr });
  } catch {}

  return { success: true };
}

// Reports go to the moderation queue. They never hide a listing by themselves:
// accounts are free and IP headers can be spoofed, so automatic hiding would let
// anyone take down a competitor's listings. Only a moderator hides or rejects.
export async function reportProperty(propertyId: string, reason?: string) {
  if (!isUuid(propertyId)) {
    return { success: false, error: 'Listing not found' };
  }

  const headersList = await headers();
  const ip = ipFromHeaders((name) => headersList.get(name));
  if (await isIpBanned(ip)) {
    return { success: false, error: 'Listing not found' };
  }
  // Reports are free to file and land in a human's queue, so one connection
  // cannot bury the moderator in them.
  if (!(await rateLimit(`report:${ip}`, 20, 24 * 60 * 60 * 1000)).ok) {
    return { success: true, action: 'queued' as const };
  }
  const session = await getSession();
  const uid = session?.userId || null;
  const cleanReason = (typeof reason === 'string' ? reason : '').trim().slice(0, 1000) || 'Unspecified reason';

  const propRes = await query(`SELECT id FROM properties WHERE id = $1 AND status IN ('active', 'sold')`, [propertyId]);
  if (propRes.rows.length === 0) {
    return { success: false, error: 'Listing not found' };
  }

  // One open report per visitor per listing.
  const duplicate = await query(
    `SELECT 1 FROM complaints
     WHERE property_id = $1 AND (ip = $2 OR ($3::uuid IS NOT NULL AND user_id = $3::uuid))
     LIMIT 1`,
    [propertyId, ip, uid]
  );
  if (duplicate.rows.length === 0) {
    await query(
      `INSERT INTO complaints (property_id, ip, user_id, reason) VALUES ($1, $2, $3, $4)`,
      [propertyId, ip, uid, cleanReason]
    );
    await query(
      `UPDATE properties SET is_reported = TRUE, reported_at = COALESCE(reported_at, NOW()) WHERE id = $1`,
      [propertyId]
    );
  }

  return { success: true, action: 'queued' };
}

// The search box's suggestions (lib/search-suggest.ts). Any visitor calls this
// on every pause in typing, so it is rate limited - checked alongside the
// queries rather than before them, so it adds no wait.
export async function getSearchSuggestions(inputText: string, agencyId?: string, places?: { city: string; province: string }[], deal?: 'sale' | 'rent') {
  if (agencyId && !isUuid(agencyId)) return EMPTY_SUGGESTIONS;
  try {
    const headersList = await headers();
    const ip = ipFromHeaders((name) => headersList.get(name));
    const [limited, suggestions] = await Promise.all([
      rateLimit(`search-suggest:${ip}`, SUGGEST_PER_MINUTE, 60_000),
      searchSuggestions(inputText, agencyId, places, deal),
    ]);
    return limited.ok ? suggestions : EMPTY_SUGGESTIONS;
  } catch (err) {
    console.error('Error fetching search suggestions:', err);
    return EMPTY_SUGGESTIONS;
  }
}

// A fast typist pauses a few times a second at most; this is generous for a
// person and a wall for a script.
const SUGGEST_PER_MINUTE = 120;

export async function getModerationQueue() {
  const auth = await verifyAdmin();
  if (!auth.authorized) return [];

  const res = await query(
    `SELECT p.*, 
            -- What the moderator is shown is one version of the listing; the decision names it (see moderationConflict).
            p.updated_at::text AS updated_token,
            owner.id AS owner_id, owner.email AS owner_email, owner.name AS owner_name,
            COALESCE((SELECT array_agg(ph.url ORDER BY ph.display_order, ph.id) FROM photos ph WHERE ph.property_id = p.id), ARRAY[]::text[]) as photos,
            COALESCE(
              (SELECT json_agg(json_build_object('id', c.id, 'reason', c.reason, 'ip', c.ip, 'created_at', c.created_at)) 
               FROM complaints c 
               WHERE c.property_id = p.id), 
              '[]'::json
            ) as complaints
     FROM properties p
     LEFT JOIN users owner ON owner.id = p.user_id
     WHERE p.deleted_at IS NULL AND (p.status = 'pending' OR EXISTS(SELECT 1 FROM complaints WHERE property_id = p.id))
     ORDER BY p.created_at ASC`
  );
  return res.rows;
}

// The agency's own listings. A consultant sees the whole catalogue but can only
// act on the listings they wrote, so each row says whether it is editable.
export async function getAgencyProperties(agencyId: string) {
  try {
    const ctx = await getAgencyContext(agencyId);
    if (!ctx) {
      return { success: false, error: 'Forbidden', properties: [] };
    }
    const res = await query(`
      SELECT p.*, COALESCE(
        (SELECT json_agg(ph.url ORDER BY ph.display_order, ph.id) FROM photos ph WHERE ph.property_id = p.id),
        '[]'::json
      ) as photos,
      author.name AS created_by_name,
      p.updated_at::text AS updated_token,
      -- The date as written, for the form (a JavaScript Date can move a day).
      to_char(p.available_from, 'YYYY-MM-DD') AS available_from
      FROM properties p
      LEFT JOIN users author ON author.id = p.created_by
      WHERE p.user_id = $1 AND p.status != 'deleted'
      ORDER BY p.created_at DESC
    `, [ctx.agencyId]);

    const properties = res.rows.map((row: any) => ({
      ...row,
      can_edit: canManageListing(ctx, row),
    }));
    return { success: true, properties, role: ctx.role };
  } catch (error: any) {
    console.error('Error fetching agency properties:', error);
    return { success: false, error: 'Could not load the agency listings', properties: [] };
  }
}

export async function saveAgencyProperty(agencyId: string, property: any) {
  try {
    const ctx = await getAgencyContext(agencyId);
    if (!ctx) {
      return { success: false, error: 'Forbidden: You do not have permission to manage this agency' };
    }

    const isNew = !property.id;
    const propertyId = property.id || crypto.randomUUID();

    // Listings created here go through the same checks as the public form: an
    // agency listing with no title or a price of zero is not a listing. With no
    // contact address of its own a listing takes the agency's public inquiry
    // address, so it is never saved without one (check of 2026-10-03).
    const lang = property?.lang === 'fr' ? 'fr' : 'en';
    const agencyRow = (await query(`SELECT contact_email, email FROM users WHERE id = $1`, [ctx.agencyId])).rows[0];
    // A manager or consultant writing a listing is its contact: their own address, not the agency's
    // (inquiries and revealed contacts went to the owner's address for every listing).
    const ownAddress = ctx.role !== 'owner' && !ctx.isAdmin ? String(ctx.email || '').trim() : '';
    const fallbackEmail = ownAddress || String(agencyRow?.contact_email || agencyRow?.email || ctx.email || '').trim();
    const input = {
      ...property,
      type: property.property_type || property.type,
      contact_email: String(property.contact_email || '').trim() || fallbackEmail,
    };
    const errors = validateListing(input, lang);
    const firstError = Object.values(errors)[0];
    if (firstError) {
      return { success: false, error: firstError, errors };
    }
    if (Array.isArray(property.photos) && property.photos.length > MAX_LISTING_PHOTOS) {
      return { success: false, error: `A listing can have at most ${MAX_LISTING_PHOTOS} photos.` };
    }
    // The values as stored: the province as its two-letter code, money as
    // dollars however it was typed, one language copied into the other.
    const n = normalizeListing(input);
    const slug = makeListingSlug(n.title_en, n.title_fr);

    // The server decides the status; the browser's value is only a request.
    let status: string | null;
    let currentSpecialist: unknown = null;
    let resubmitted = false;
    // An agency publishes only in a place the site knows (a census municipality
    // or one an administrator added); the form offers to ask for any other.
    // A listing already saved keeps its place unless the place is changed.
    const unknownPlace = (): { success: false; error: string; cityUnknown: true; city: string; province: string; id?: undefined; status?: undefined; resubmitted?: undefined; errors?: undefined; duplicate?: undefined } =>
      ({ success: false, error: UNKNOWN_PLACE_ERROR, cityUnknown: true, city: n.city, province: n.province });

    if (isNew) {
      if (!ctx.isAdmin && !(await isKnownPlace(n.city, n.province))) return unknownPlace();
      // The same listing twice is nearly always a double click or a forgotten
      // copy; the form asks, and sends it again with confirmDuplicate.
      if (property.confirmDuplicate !== true) {
        const twin = await query(
          `SELECT 1 FROM properties
           WHERE user_id = $1 AND status <> 'deleted' AND LOWER(title_en) = LOWER($2)
             AND LOWER(COALESCE(address, '')) = LOWER($3) AND LOWER(city) = LOWER($4) AND price = $5
           LIMIT 1`,
          [ctx.agencyId, n.title_en, n.address || '', n.city, n.price]
        );
        if (twin.rows[0]) {
          return { success: false, duplicate: true, error: 'You already have a listing with the same title, address, city and price.' };
        }
      }
      status = (await canAutoPublish(ctx.agencyId, ctx.session)) ? 'active' : 'pending';
    } else {
      if (!isUuid(propertyId)) {
        return { success: false, error: 'Listing not found' };
      }
      const currentRes = await query(
        `SELECT status, approved_at, is_hidden_by_admin, deleted_at, user_id, created_by, assigned_specialist, city, province, updated_at::text AS updated_token FROM properties WHERE id = $1 AND user_id = $2`,
        [propertyId, ctx.agencyId]
      );
      const current = currentRes.rows[0];
      if (!current) {
        return { success: false, error: 'Listing not found' };
      }
      const samePlace = String(current.city || '').trim().toLowerCase() === n.city.trim().toLowerCase()
        && String(current.province || '') === n.province;
      if (!ctx.isAdmin && !samePlace && !(await isKnownPlace(n.city, n.province))) return unknownPlace();
      // A consultant edits only the listings they wrote.
      if (!canManageListing(ctx, current)) {
        return { success: false, error: 'This listing belongs to another member of your team' };
      }
      // A listing in the trash, or one a moderator has hidden, is not the
      // agency's to edit (as for a private seller, updateProperty).
      if (!ctx.isAdmin) {
        if (current.deleted_at || current.status === 'deleted') {
          return { success: false, error: 'This listing has been deleted' };
        }
        if (current.is_hidden_by_admin) {
          return { success: false, error: 'This listing was hidden by moderation. Contact support to have it restored.' };
        }
      }
      // Two people (or two tabs) editing one listing: the later save would silently undo the
      // earlier one. The form sends the version it opened; a different one means someone saved since.
      if (typeof property.updated_token === 'string' && property.updated_token && property.updated_token !== current.updated_token) {
        return { success: false, error: 'This listing was changed by someone else while you were editing it. Close the form, reload the list and make your changes again.' };
      }
      currentSpecialist = current.assigned_specialist;
      status = null;
      if (!ctx.isAdmin && current.status === 'rejected') {
        // Fixing a rejected listing and saving it is the way to ask moderation
        // for another look; before this it stayed rejected for good. The status
        // in the form is not a request here.
        status = 'pending';
        resubmitted = true;
      } else if (property.status && property.status !== current.status) {
        if (ctx.isAdmin) {
          status = property.status;
        } else {
          const denied = ownerStatusChangeError(current, property.status);
          if (denied) {
            return { success: false, error: denied };
          }
          status = property.status;
        }
      }
    }

    // An agency that is not approved (pending, rejected) is not trusted to change a
    // live listing on its own: the edit sends it back through moderation, as it does
    // for a private seller. An approved agency, an auto-approved account and an
    // administrator edit freely.
    if (!isNew && !ctx.isAdmin && !(await canEditWithoutReview(ctx.agencyId, ctx.session))) {
      if ((status ?? (await query(`SELECT status FROM properties WHERE id = $1`, [propertyId])).rows[0]?.status) === 'active') {
        status = 'pending';
      }
    }

    // Only a specialist from the agency's own team page can be shown on a listing.
    const specialistJson = await resolveSpecialist(ctx.agencyId, property.assigned_specialist, currentSpecialist);

    if (isNew) {
      await query(`
        INSERT INTO properties (
          id, user_id, created_by, status, title_en, title_fr, description_en, description_fr,
          price, property_type, bedrooms, bathrooms, pet_friendly, province, city,
          address, slug, contact_email, contact_phone, living_area, year_built, amenities,
          condo_fees, garage_spaces, driveway_spaces, assigned_specialist, approved_at,
          ${RENTAL_COLUMNS.join(', ')}
        ) VALUES (
          $1, $2, $26, $3::text, $4, $5, $6, $7,
          $8, $9, $10, $11, $12, $13, $14,
          $15, $16, $17, $18, $19, $20, $21,
          $22, $23, $24, $25, CASE WHEN $3::text = 'active' THEN NOW() ELSE NULL END,
          ${rentalParams(27)}
        )
      `, [
        propertyId, ctx.agencyId, status, n.title_en, n.title_fr, n.description_en, n.description_fr,
        n.price, n.type, n.bedrooms, n.bathrooms, n.pet_friendly, n.province, n.city,
        n.address || '', slug, n.contact_email, n.contact_phone || '', n.living_area, n.year_built, n.amenities,
        n.condo_fees, n.garage_spaces, n.driveway_spaces,
        specialistJson,
        ctx.userId,
        ...rentalValues(input),
      ]);
    } else {
      await query(`
        UPDATE properties
        SET title_en = $1, title_fr = $2, description_en = $3, description_fr = $4,
            price = $5, property_type = $6, bedrooms = $7, bathrooms = $8, pet_friendly = $9,
            province = $10, city = $11, address = $12, contact_email = $13, contact_phone = $14,
            living_area = $15, year_built = $16, amenities = $17, condo_fees = $18,
            garage_spaces = $19, driveway_spaces = $20, assigned_specialist = $21,
            status = COALESCE($22::text, status),
            approved_at = CASE WHEN $22::text = 'active' THEN COALESCE(approved_at, NOW())
                               WHEN $22::text IN ('pending', 'rejected') THEN NULL
                               ELSE approved_at END,
            updated_at = NOW()${status ? activationExpirySql(status) : ''},
            ${rentalSet(25)}
        WHERE id = $23 AND user_id = $24
      `, [
        n.title_en, n.title_fr, n.description_en, n.description_fr,
        n.price, n.type, n.bedrooms, n.bathrooms, n.pet_friendly,
        n.province, n.city, n.address || '', n.contact_email, n.contact_phone || '',
        n.living_area, n.year_built, n.amenities, n.condo_fees,
        n.garage_spaces, n.driveway_spaces,
        specialistJson,
        status, propertyId, ctx.agencyId,
        ...rentalValues(input),
      ]);
    }

    // One statement, so a failure part way through cannot leave the listing
    // with its old photographs deleted and only some of the new ones written.
    await replaceListingPhotos(propertyId, property.photos);

    // What the listing is now, so the form can say whether it went live or is
    // waiting for moderation instead of always announcing "saved".
    const saved = await query(`SELECT status FROM properties WHERE id = $1`, [propertyId]);
    return { success: true, id: propertyId, status: saved.rows[0]?.status as string | undefined, resubmitted };
  } catch (error: any) {
    console.error('Error saving agency property:', error);
    return { success: false, error: publicError(error) };
  }
}

export async function deleteAgencyProperty(propertyId: string, agencyId: string) {
  try {
    const ctx = await getAgencyContext(agencyId);
    if (!ctx || !isUuid(propertyId)) {
      return { success: false, error: 'Forbidden: You do not have permission to manage this agency' };
    }

    const current = await query(`SELECT user_id, created_by FROM properties WHERE id = $1`, [propertyId]);
    if (!current.rows[0] || !canManageListing(ctx, current.rows[0])) {
      return { success: false, error: 'Forbidden: You do not have permission to delete this listing' };
    }

    await query(`
      UPDATE properties
      SET status = 'deleted', deleted_at = NOW(), updated_at = NOW()
      WHERE id = $1 AND user_id = $2
    `, [propertyId, ctx.agencyId]);

    try {
      const { createAuditLog } = await import('@/lib/audit');
      await createAuditLog('delete_agency_property', 'property', propertyId, { agencyId: ctx.agencyId, by: ctx.email });
    } catch {}

    return { success: true };
  } catch (error: any) {
    console.error('Error deleting agency property:', error);
    return { success: false, error: 'Could not delete this listing' };
  }
}

export async function bulkImportAgencyProperties(agencyId: string, propertiesList: any[]) {
  try {
    const ctx = await getAgencyContext(agencyId);
    if (!ctx) {
      return { success: false, error: 'Forbidden: You do not have permission to manage this agency' };
    }

    if (!Array.isArray(propertiesList) || propertiesList.length === 0) {
      return { success: false, error: 'Empty property list' };
    }
    if (propertiesList.length > 500) {
      return { success: false, error: 'Import at most 500 listings at a time' };
    }

    const userRow = await query(`SELECT auto_approve_listings, contact_email, email FROM users WHERE id = $1`, [ctx.agencyId]);
    const autoApprove = await canAutoPublish(ctx.agencyId, ctx.session);
    const defaultEmail = userRow.rows[0]?.contact_email || userRow.rows[0]?.email || ctx.email;

    // What this agency already has, so importing the same file twice does not
    // silently make a second copy of everything in it.
    const existingRows = await query<{ title_en: string; address: string; city: string; price: string }>(
      `SELECT title_en, COALESCE(address, '') AS address, city, price
       FROM properties WHERE user_id = $1 AND status <> 'deleted'`,
      [ctx.agencyId]
    );
    const seen = new Set(
      existingRows.rows.map((r) => importIdentity({ title: r.title_en, address: r.address, city: r.city, price: r.price }))
    );

    let importedCount = 0;
    let duplicateCount = 0;
    const errors: string[] = [];
    const knownPlaces = new Map<string, boolean>();
    const unknownPlaces: { city: string; province: string }[] = [];

    for (let idx = 0; idx < propertiesList.length; idx++) {
      const p = propertiesList[idx];
      const titleEn = (p.title_en || p.title || `Property ${idx + 1}`).trim();
      const titleFr = (p.title_fr || p.title || titleEn).trim();
      // Number() is NaN for every way money is actually written in a
      // spreadsheet - "$450,000", "450 000", "450 000,50" - and NaN fell
      // through `|| 0` to zero, so the row failed with "Enter the asking
      // price" and an agency importing its own export got five hundred
      // identical errors with nothing to explain them.
      const price = importNumber(p.price) ?? 0;
      // What the row says, and nothing made up: a row without a city, a
      // province or a type it names is reported, not put in Montreal as a house.
      const city = String(p.city ?? '').trim();
      // "Ontario", "ON" and "on" are one province; the code is what is stored.
      const provinceText = String(p.province ?? '').trim();
      const province = findProvince(provinceText)?.code ?? provinceText.toUpperCase();
      const propertyType = knownPropertyType(p.property_type || p.type) ?? '';
      const bedrooms = importInteger(p.bedrooms) ?? 0;
      const bathrooms = importNumber(p.bathrooms) ?? 0;
      const address = (p.address || '').trim();
      const descriptionEn = (p.description_en || p.description || '').trim();
      const descriptionFr = (p.description_fr || p.description || descriptionEn).trim();
      const livingArea = importNumber(p.living_area);
      const yearBuilt = importInteger(p.year_built);
      const condoFees = importNumber(p.condo_fees);
      const petFriendly = importBoolean(p.pet_friendly);
      // "Pool, EV Charging" or a list: stored as the site's own keys, unknown words left out.
      const amenities = (Array.isArray(p.amenities) ? p.amenities : (typeof p.amenities === 'string' ? p.amenities.split(',') : []))
        .map(canonicalAmenity)
        .filter((a: string | null): a is string => a !== null);
      // Sale unless the row says rent ("rent", "location", "à louer").
      const rental = {
        listing_type: listingTypeOf(p.listing_type),
        province,
        available_from: p.available_from,
        lease_term: typeof p.lease_term === 'string' ? p.lease_term.trim().toLowerCase() : p.lease_term,
        furnished: typeof p.furnished === 'string' ? p.furnished.trim().toLowerCase() : p.furnished,
        rent_includes: Array.isArray(p.rent_includes) ? p.rent_includes : typeof p.rent_includes === 'string' ? p.rent_includes.split(/[,;|]/).map((s: string) => s.trim().toLowerCase()).filter(Boolean) : [],
        deposit_amount: importNumber(p.deposit_amount) != null ? Math.round(importNumber(p.deposit_amount)!) : '',
        price,
      };

      // Photos are not taken from the spreadsheet: a link to someone else's
      // server skips the whole upload pipeline and breaks when they delete it.
      // They are attached afterwards from the agency's own uploads.
      const propId = crypto.randomUUID();
      const slug = makeListingSlug(titleEn, titleFr);
      const status = autoApprove ? 'active' : 'pending';

      const rowErrors = validateListing({
        ...rental,
        province, city, contact_email: defaultEmail, type: propertyType, price,
        bedrooms, bathrooms, living_area: livingArea, year_built: yearBuilt,
        title_en: titleEn, title_fr: titleFr, description_en: descriptionEn, description_fr: descriptionFr,
      }, 'en');
      const firstRowError = Object.values(rowErrors)[0];
      if (firstRowError) {
        errors.push(`Row ${idx + 1} (${titleEn}): ${firstRowError}`);
        continue;
      }

      // Only a place the site knows; the rows left out are listed so the agency
      // can ask for the place to be added.
      const placeKey = `${province}|${city.toLowerCase()}`;
      let placeKnown = knownPlaces.get(placeKey);
      if (placeKnown === undefined) {
        placeKnown = await isKnownPlace(city, province);
        knownPlaces.set(placeKey, placeKnown);
      }
      if (!placeKnown) {
        errors.push(`Row ${idx + 1} (${titleEn}): ${city}, ${province} is not in our list of places yet - ask the administrator to add it`);
        if (!unknownPlaces.some((u) => u.city.toLowerCase() === city.toLowerCase() && u.province === province)) unknownPlaces.push({ city, province });
        continue;
      }

      const identity = importIdentity({ title: titleEn, address, city, price });
      if (seen.has(identity)) {
        duplicateCount++;
        errors.push(`Row ${idx + 1} (${titleEn}): already in your listings, skipped`);
        continue;
      }
      seen.add(identity);

      try {
        await query(`
          INSERT INTO properties (
            id, user_id, created_by, status, title_en, title_fr, description_en, description_fr,
            price, property_type, bedrooms, bathrooms, pet_friendly, province, city,
            address, slug, contact_email, living_area, year_built, amenities,
            condo_fees, approved_at, ${RENTAL_COLUMNS.join(', ')}
          ) VALUES (
            $1, $2, $22, $3::text, $4, $5, $6, $7,
            $8, $9, $10, $11, $12, $13, $14,
            $15, $16, $17, $18, $19, $20,
            $21, CASE WHEN $3::text = 'active' THEN NOW() ELSE NULL END, ${rentalParams(23)}
          )
        `, [
          propId, ctx.agencyId, status, titleEn, titleFr, descriptionEn, descriptionFr,
          price, propertyType, bedrooms, bathrooms, petFriendly, province, city,
          address, slug, defaultEmail, livingArea, yearBuilt, amenities,
          condoFees, ctx.userId,
          ...rentalValues(rental),
        ]);

        importedCount++;
      } catch {
        errors.push(`Row ${idx + 1} (${titleEn}): could not be imported`);
      }
    }

    try {
      const { createAuditLog } = await import('@/lib/audit');
      await createAuditLog('bulk_import_agency_properties', 'property', ctx.agencyId, {
        agencyId: ctx.agencyId,
        total: propertiesList.length,
        importedCount,
        duplicateCount,
        errorsCount: errors.length
      });
    } catch {}

    return { success: true, count: importedCount, total: propertiesList.length, duplicates: duplicateCount, errors, unknownPlaces };
  } catch (error: any) {
    console.error('Error during bulk import:', error);
    return { success: false, error: publicError(error) };
  }
}


// One spelling for a value from the database and the same value from the edit
// form: empty is null, numbers compare as numbers ("2.0" = 2), a date as its
// calendar day, a list by its members.
function sameValue(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${v.getFullYear()}-${p(v.getMonth() + 1)}-${p(v.getDate())}`;
  }
  if (Array.isArray(v)) return v.length ? JSON.stringify(v.map(String).sort()) : null;
  if (typeof v === 'object') return JSON.stringify(v);
  if (typeof v === 'boolean') return String(v);
  const text = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  return text !== '' && Number.isFinite(Number(text)) ? String(Number(text)) : text;
}

export async function updateProperty(id: string, data: any) {
  const auth = await verifyAdminOrOwner(id);
  if (!auth.authorized) {
    return { success: false, error: auth.error };
  }
  
  const session = await getSession();

  // A listing in the trash, or one a moderator has hidden, is not the owner's to edit.
  // Editing it sent it back to the moderation queue with its trash mark still on, and
  // an approval there would have brought it back to life.
  let wasRejected = false;
  if (!auth.isAdmin) {
    const state = (await query(
      `SELECT status, deleted_at, is_hidden_by_admin FROM properties WHERE id = $1`, [id]
    )).rows[0];
    // Say which: a deleted listing and one a moderator hid need different next steps.
    if (state && (state.deleted_at || state.status === 'deleted')) {
      return { success: false, error: 'This listing has been deleted' };
    }
    if (state && state.is_hidden_by_admin) {
      return { success: false, error: 'This listing was hidden by moderation. Contact support to have it restored.' };
    }
    wasRejected = state?.status === 'rejected';
  }

  const lang = data?.lang === 'fr' ? 'fr' : 'en';
  const errors = validateListing(data || {}, lang);
  const firstError = Object.values(errors)[0];
  if (firstError) {
    return { success: false, error: firstError, errors };
  }
  const input = normalizeListing(data);

  // The place may stay as it is; changing it needs a place the site knows.
  if (!auth.isAdmin) {
    const was = (await query(`SELECT city, province FROM properties WHERE id = $1`, [id])).rows[0];
    const samePlace = was
      && String(was.city || '').trim().toLowerCase() === input.city.trim().toLowerCase()
      && String(was.province || '') === input.province;
    if (!samePlace && !(await isKnownPlace(input.city, input.province))) {
      return { success: false, error: UNKNOWN_PLACE_ERROR, cityUnknown: true, city: input.city, province: input.province };
    }
  }

  // Edits go back through moderation unless the account is trusted (admin,
  // approved agency, or auto-approve). Clearing approved_at stops the owner from
  // re-activating the edited listing before a moderator has looked at it.
  // A price alone does not (owner's decision, 2026-09-27): a seller who lowers
  // the price stays online. Anything else in the listing does, and so does a
  // new photo (app/api/upload/route.ts); status changes never did.
  const shouldResetStatus = !auth.isAdmin && !(await canEditWithoutReview(session?.userId, session));
  let review = false;
  if (shouldResetStatus) {
    const old = (await query(`SELECT * FROM properties WHERE id = $1`, [id])).rows[0] || {};
    // The phone is written in one shape (+1 (514) 555-0101); a listing stored by an older version or an import in another shape is
    // the same number, and a seller who only changed the price must not be sent to review because of how the number was spelt.
    if (old.contact_phone) old.contact_phone = formatCanadianPhone(String(old.contact_phone));
    const rental = rentalValues(input);
    const written: Array<[string, unknown]> = [
      ['title_en', input.title_en], ['title_fr', input.title_fr],
      ['description_en', input.description_en], ['description_fr', input.description_fr],
      ['property_type', input.type], ['bedrooms', input.bedrooms], ['bathrooms', input.bathrooms],
      ['pet_friendly', input.pet_friendly], ['province', input.province], ['city', input.city],
      ['address', input.address], ['contact_email', input.contact_email], ['contact_phone', input.contact_phone],
      ['living_area', input.living_area], ['year_built', input.year_built], ['amenities', input.amenities],
      ...RENTAL_COLUMNS.map((c, i) => [c, rental[i]] as [string, unknown]),
    ];
    review = written.some(([column, value]) => sameValue(old[column]) !== sameValue(value));
  }

  // Fixing a rejected listing and saving it is the way to ask for another look, whatever was changed (a price
  // alone used to leave it rejected while the screen said "Changes saved").
  if (wasRejected) review = true;

  await query(
    `UPDATE properties 
     SET 
       title_en = $1, title_fr = $2, 
       description_en = $3, description_fr = $4,
       price = $5, property_type = $6, 
       bedrooms = $7, bathrooms = $8,
       pet_friendly = $9, province = $10, city = $11,
       slug = CASE WHEN slug LIKE 'draft-%' THEN $12 ELSE slug END, address = $13, contact_email = $14, contact_phone = $15,
       require_auth_to_contact = $16,
       living_area = $17, year_built = $18, amenities = $19,
       ${rentalSet(21)}
       , updated_at = NOW()
       ${review ? ", status = 'pending', rejection_reason = NULL, approved_at = NULL" : ""}
     WHERE id = $20`,
    [
      input.title_en, input.title_fr,
      input.description_en, input.description_fr,
      input.price, input.type,
      input.bedrooms, input.bathrooms,
      input.pet_friendly, input.province, input.city,
      makeListingSlug(input.title_en, input.title_fr),
      input.address, input.contact_email, input.contact_phone,
      false,
      input.living_area,
      input.year_built,
      input.amenities,
      id,
      ...rentalValues(input),
    ]
  );
  return { success: true, status: review ? 'pending' : undefined };
}

export async function deleteProperty(id: string) {
  const auth = await verifyAdminOrOwner(id);
  if (!auth.authorized) {
    return { success: false, error: auth.error };
  }

  // Soft delete: hidden everywhere (including the owner's list); admins can restore it from the trash.
  await query(`UPDATE properties SET deleted_at = NOW(), status = 'deleted', updated_at = NOW() WHERE id = $1`, [id]);
  
  try {
    const { createAuditLog } = await import('@/lib/audit');
    await createAuditLog('delete_property', 'property', id, { soft_delete: true, by: auth.email });
  } catch {}

  return { success: true };
}

// Owner/admin, or anyone holding the id of a not-yet-published draft: the listing
// form uploads and arranges photos before the author has confirmed their email.
async function verifyPhotoAccess(propertyId: string): Promise<{ authorized: boolean; isAdmin?: boolean; error?: string }> {
  if (!isUuid(propertyId)) return { authorized: false, error: 'Property not found' };
  const owner = await verifyAdminOrOwner(propertyId);
  if (owner.authorized) return owner;
  const res = await query(`SELECT status, user_id, approved_at FROM properties WHERE id = $1`, [propertyId]);
  if (res.rows[0] && isUnclaimedDraft(res.rows[0])) {
    return { authorized: true, isAdmin: false };
  }
  return owner;
}

// The photos of a listing as its editor sees them (the uploader of the edit form). A stranger asking for the photos of a
// listing that is not online - pending, rejected, hidden by moderation, a draft - used to get them by its id.
export async function getPropertyPhotos(propertyId: string) {
  if (!isUuid(propertyId)) return [];
  const auth = await verifyPhotoAccess(propertyId);
  if (!auth.authorized) return [];
  const res = await query(
    `SELECT id, url, file_hash, created_at, COALESCE(display_order, 0) as display_order 
     FROM photos 
     WHERE property_id = $1 
     ORDER BY COALESCE(display_order, 0) ASC, created_at ASC`,
    [propertyId]
  );
  return res.rows;
}

export async function reorderPropertyPhotos(propertyId: string, photoIds: string[]) {
  try {
    const auth = await verifyPhotoAccess(propertyId);
    if (!auth.authorized) {
      return { success: false, error: auth.error };
    }

    // A listing has a few dozen photographs; more than that is not a reordering.
    const ordered = (Array.isArray(photoIds) ? photoIds : []).filter(isUuid).slice(0, MAX_LISTING_PHOTOS);
    for (let i = 0; i < ordered.length; i++) {
      await query(`UPDATE photos SET display_order = $1 WHERE id = $2 AND property_id = $3`, [i, ordered[i], propertyId]);
    }

    // Also update properties.photos array to keep them in sync
    const updatedPhotosRes = await query(
      `SELECT url FROM photos WHERE property_id = $1 ORDER BY COALESCE(display_order, 0) ASC, created_at ASC`,
      [propertyId]
    );
    const photoUrls = updatedPhotosRes.rows.map((r: any) => r.url);
    await query(`UPDATE properties SET photos = $1, updated_at = NOW() WHERE id = $2`, [photoUrls, propertyId]);

    return { success: true, photoUrls };
  } catch (error: any) {
    console.error('Error reordering property photos:', error);
    return { success: false, error: publicError(error) };
  }
}

export async function deletePropertyPhoto(photoId: string, propertyId: string) {
  const auth = await verifyPhotoAccess(propertyId);
  if (!auth.authorized) {
    return { success: false, error: auth.error };
  }

  // The last photo of a listing that is online stays (an administrator may still remove it).
  if (!auth.isAdmin) {
    const live = await query(
      `SELECT (p.status = 'active' AND p.approved_at IS NOT NULL) AS live, (SELECT count(*)::int FROM photos WHERE property_id = p.id) AS n FROM properties p WHERE p.id = $1`,
      [propertyId]
    );
    if (live.rows[0]?.live && Number(live.rows[0].n) <= 1) {
      return { success: false, error: 'A listing that is online keeps at least one photo.' };
    }
  }

  const gone = await query<{ url: string }>(
    `DELETE FROM photos WHERE id = $1 AND property_id = $2 RETURNING url`,
    [photoId, propertyId]
  );
  // The row used to be all that went; the file stayed on disk, reachable at its
  // old address, for good.
  await removeUnreferencedUploads(gone.rows.map((r) => r.url));
  return { success: true };
}

export async function getAllAdminProperties(limit = 100, offset = 0, statusFilter = '', reportedOnly = false, search = '') {
  try {
    const auth = await verifyAdmin();
    if (!auth.authorized) {
      return { success: false, properties: [], total: 0, error: auth.error };
    }

    const whereClauses: string[] = [];
    const values: any[] = [];

    // The empty rows the listing form leaves when somebody stops at the photos step are not listings: they have a
    // list of their own, and the rest of the directory does not carry them (they were over half of it).
    const isDraft = `(p.slug LIKE 'draft-%' AND (p.title_en IS NULL OR p.title_en = 'Draft') AND (p.title_fr IS NULL OR p.title_fr = 'Brouillon'))`;
    if (statusFilter === 'drafts') {
      whereClauses.push(isDraft);
    } else {
      whereClauses.push(`NOT ${isDraft}`);
      if (statusFilter && statusFilter !== 'all') {
        whereClauses.push(`p.status = $${values.length + 1}`);
        values.push(statusFilter);
      }
    }

    if (reportedOnly) {
      whereClauses.push(`EXISTS(SELECT 1 FROM complaints c WHERE c.property_id = p.id)`);
    }

    // The whole table, not the page on screen: the filter used to look only at
    // the 50 listings loaded, so a listing on page 8 could not be found by its
    // title (admin check, 2026-09-27). % and _ are taken literally.
    const q = String(search || '').trim().slice(0, 200);
    if (q) {
      values.push(`%${q.replace(/[\\%_]/g, (c) => '\\' + c)}%`);
      const n = values.length;
      whereClauses.push(`(p.title_en ILIKE $${n} OR p.title_fr ILIKE $${n} OR p.city ILIKE $${n} OR p.address ILIKE $${n} OR p.slug ILIKE $${n} OR p.contact_email ILIKE $${n})`);
    }

    const whereSql = whereClauses.length > 0 ? ` WHERE ${whereClauses.join(' AND ')}` : '';

    const sql = `
      SELECT p.*, 
             COALESCE((SELECT array_agg(ph.url ORDER BY ph.display_order, ph.id) FROM photos ph WHERE ph.property_id = p.id), ARRAY[]::text[]) as photos,
             (SELECT COUNT(*)::int FROM complaints c WHERE c.property_id = p.id) as complaints_count
      FROM properties p
      ${whereSql}
      ORDER BY p.created_at DESC, p.id
      LIMIT $${values.length + 1} OFFSET $${values.length + 2}
    `;

    const countSql = `SELECT COUNT(*)::int as total FROM properties p ${whereSql}`;
    const countRes = await query(countSql, values);
    const total = countRes.rows[0]?.total || 0;

    values.push(limit, offset);
    const res = await query(sql, values);
    return { success: true, properties: res.rows, total };
  } catch (error: any) {
    console.error('Error fetching all admin properties:', error);
    return { success: false, properties: [], total: 0, error: publicError(error) };
  }
}

export async function adminSaveProperty(id: string, data: any) {
  try {
    const auth = await verifyAdmin();
    if (!auth.authorized) {
      return { success: false, error: auth.error };
    }

    // Keep the published URL unless an explicit new slug is given.
    const slug = typeof data.slug === 'string' && data.slug.trim() ? data.slug.trim() : null;
    
    await query(
      `UPDATE properties 
       SET 
         title_en = $1, title_fr = $2, 
         description_en = $3, description_fr = $4,
         price = $5, property_type = $6, 
         bedrooms = $7, bathrooms = $8,
         pet_friendly = $9, province = $10, city = $11,
         slug = COALESCE($12, slug), address = $13, contact_email = $14, contact_phone = $15,
         living_area = $16, year_built = $17,
         -- The status carries what it means, as it does when the moderator sets it from the list (updatePropertyStatus): going live
         -- is an approval (with the lifetime a listing gets when it goes live), waiting or refused is not. It used to change the word
         -- only, and a listing made "Active" here had no approval date: its owner could not resume it after a pause, could not
         -- extend it, and it never expired.
         expires_at = CASE WHEN $18::text = 'active' AND approved_at IS NULL THEN ${EXPIRY_ON_ACTIVATION_SQL} ELSE expires_at END,
         approved_at = CASE WHEN $18::text = 'active' THEN COALESCE(approved_at, NOW())
                            WHEN $18::text IN ('pending', 'rejected') THEN NULL
                            ELSE approved_at END,
         status = $18,
         -- The kind, when the moderator changes it; a rental's terms are the
         -- owner's and stay, except what a sale or Quebec cannot have.
         listing_type = COALESCE($20, listing_type),
         available_from = CASE WHEN COALESCE($20, listing_type) = 'rent' THEN available_from END,
         lease_term = CASE WHEN COALESCE($20, listing_type) = 'rent' THEN lease_term END,
         furnished = CASE WHEN COALESCE($20, listing_type) = 'rent' THEN furnished END,
         rent_includes = CASE WHEN COALESCE($20, listing_type) = 'rent' THEN rent_includes END,
         deposit_amount = CASE WHEN COALESCE($20, listing_type) = 'rent' AND $10::varchar <> 'QC' THEN deposit_amount END,
         updated_at = NOW()
       WHERE id = $19`,
      [
        data.title_en || '',
        data.title_fr || '',
        data.description_en || '',
        data.description_fr || '',
        Number(data.price) || 0,
        normalizePropertyType(data.property_type || data.type),
        Number(data.bedrooms) || 0,
        Number(data.bathrooms) || 0,
        Boolean(data.pet_friendly),
        data.province || 'QC',
        data.city || 'Montreal',
        slug,
        data.address || '',
        data.contact_email || '',
        data.contact_phone || '',
        data.living_area ? Number(data.living_area) : null,
        data.year_built ? Number(data.year_built) : null,
        data.status || 'active',
        id,
        data.listing_type === undefined ? null : listingTypeOf(data.listing_type),
      ]
    );

    // Only when the caller sent a list at all: an edit that does not mention
    // photographs must not clear them.
    if (Array.isArray(data.photos)) {
      await replaceListingPhotos(id, data.photos);
    }

    // What a moderator rewrites in somebody's listing is on the record: who, which listing, what it was set to.
    try {
      const { createAuditLog } = await import('@/lib/audit');
      await createAuditLog('admin_edit_property', 'property', id, { title: data.title_en || data.title_fr || '', status: data.status || 'active' });
    } catch {}

    return { success: true };
  } catch (error: any) {
    console.error('Error saving property as admin:', error);
    return { success: false, error: publicError(error) };
  }
}


